// Agrupa resultados de busca por sócio em pessoas: a mesma pessoa que aparece
// em várias empresas (ou em várias fontes) vira um cartão só, com a lista de
// empresas dentro. Pessoas de mesmo nome só são separadas quando as máscaras
// públicas de CPF conflitam, o sinal mais confiável de que são homônimos.
//
// Nem todo vínculo identifica alguém. Fonte que devolve só um pedaço do nome
// ("CLARO", "MARCELO") ou nenhum nome não pode virar pessoa: agrupar por isso
// juntava 29 gentes diferentes num cartão só. Esses casos saem à parte, como
// pistas de empresa.

import type { SearchResult } from "../../shared/types";

export type GroupedPartners = {
  /** Vínculos que identificam uma pessoa, agrupados por ela. */
  groups: PersonGroup[];
  /** Vínculos sem pessoa identificável: viram pistas de empresa. */
  unidentified: SearchResult[];
};

export type PersonGroup = {
  key: string;
  nameKey: string;
  personName: string;
  maskedDocument?: string;
  maskMatch: boolean;
  sources: string[];
  companies: SearchResult[];
};

function normalizeName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Extrai os 6 dígitos centrais de uma máscara pública "***123456**". */
function maskMiddle(masked?: string): string | undefined {
  if (!masked) return undefined;
  return masked.match(/\*{2,3}\.?(\d{3})\.?(\d{3})[-.]?\*{2}/)?.slice(1).join("")
    ?? masked.match(/\*{3}(\d{6})\*{2}/)?.[1];
}

function companyKey(result: SearchResult): string {
  const cnpj = (result.cnpj ?? "").replace(/\D/g, "");
  return cnpj || normalizeName(result.name);
}

/**
 * Um vínculo só identifica alguém quando tem nome de gente (dois pedaços ou
 * mais) ou a máscara pública do CPF. "CLARO" sozinho não identifica ninguém.
 */
function identifiesPerson(result: SearchResult): boolean {
  const name = (result.partnerName ?? "").trim();
  if (!name) return false;
  if (maskMiddle(result.maskedDocument)) return true;
  return normalizeName(name).includes(" ");
}

export function groupPartnerResults(results: SearchResult[]): GroupedPartners {
  const groups: PersonGroup[] = [];
  const unidentified: SearchResult[] = [];
  for (const result of results) {
    if (!identifiesPerson(result)) {
      unidentified.push(result);
      continue;
    }
    const personName = (result.partnerName ?? result.name).trim();
    const nameKey = normalizeName(personName);
    const middle = maskMiddle(result.maskedDocument);

    let group = groups.find((candidate) => {
      if (candidate.nameKey !== nameKey) return false;
      const candidateMiddle = maskMiddle(candidate.maskedDocument);
      return !candidateMiddle || !middle || candidateMiddle === middle;
    });
    if (!group) {
      group = {
        key: `${nameKey}|${middle ?? `g${groups.length}`}`,
        nameKey,
        personName,
        maskedDocument: result.maskedDocument,
        maskMatch: Boolean(result.maskMatch),
        sources: [],
        companies: [],
      };
      groups.push(group);
    }

    if (!group.maskedDocument && result.maskedDocument) group.maskedDocument = result.maskedDocument;
    if (result.maskMatch) group.maskMatch = true;
    if (personName.length > group.personName.length) group.personName = personName;
    for (const source of result.source.split("+").map((part) => part.trim()).filter(Boolean)) {
      if (!group.sources.includes(source)) group.sources.push(source);
    }

    const key = companyKey(result);
    const existing = group.companies.find((company) => companyKey(company) === key);
    if (!existing) {
      group.companies.push({ ...result });
      continue;
    }
    // A mesma empresa veio de outra fonte: completa o que estiver faltando.
    if (!existing.cnpj && result.cnpj) existing.cnpj = result.cnpj;
    if (!existing.role && result.role) existing.role = result.role;
    if (!existing.joinedAt && result.joinedAt) existing.joinedAt = result.joinedAt;
    if (!existing.status && result.status) existing.status = result.status;
    if (!existing.city && result.city) existing.city = result.city;
    if (!existing.state && result.state) existing.state = result.state;
    if (!existing.subtitle && result.subtitle) existing.subtitle = result.subtitle;
    if (result.maskMatch) existing.maskMatch = true;
  }
  return { groups, unidentified };
}
